import React, { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  Settings,
  Save,
  Shield,
  Mail,
  Database,
  Globe,
  Lock,
  Server,
  Key,
  AlertCircle,
  Check,
  X,
  CheckCircle,
  Plus,
  Trash2,
  Edit,
  Users,
  Heart,
  Palette,
  Upload,
  Image,
  Loader2,
  Wifi,
  WifiOff
} from 'lucide-react'
import { motion } from 'framer-motion'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Switch } from '../ui/switch'
import { toast } from 'react-hot-toast'
import { cn } from '../../utils/cn'

// ── Defined at module level so their references never change between renders ──

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      staggerChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

const ModernInput = ({ label, type = 'text', value, onChange, placeholder, required = false, disabled = false }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
      {label} {required && <span className="text-error-500">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={cn(
        'w-full px-3 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg',
        'bg-white dark:bg-dark-300 text-neutral-900 dark:text-neutral-100',
        'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
        'placeholder:text-neutral-400 dark:placeholder:text-neutral-500',
        'transition-all duration-200',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    />
  </div>
)

const ModernSelect = ({ label, value, onChange, options, required = false }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
      {label} {required && <span className="text-error-500">*</span>}
    </label>
    <select
      value={value}
      onChange={onChange}
      className={cn(
        'w-full px-3 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg',
        'bg-white dark:bg-dark-300 text-neutral-900 dark:text-neutral-100',
        'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
        'transition-all duration-200'
      )}
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
)

const ModernTextarea = ({ label, value, onChange, placeholder, rows = 3 }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
      {label}
    </label>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className={cn(
        'w-full px-3 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg',
        'bg-white dark:bg-dark-300 text-neutral-900 dark:text-neutral-100',
        'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
        'placeholder:text-neutral-400 dark:placeholder:text-neutral-500',
        'transition-all duration-200 resize-none'
      )}
    />
  </div>
)

const ModernSwitchField = ({ label, description, checked, onCheckedChange }) => (
  <div className="flex items-center justify-between py-4 border-b border-neutral-100 dark:border-neutral-700/40 last:border-0">
    <div className="space-y-0.5">
      <label className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
        {label}
      </label>
      {description && (
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          {description}
        </p>
      )}
    </div>
    <Switch
      checked={checked}
      onCheckedChange={onCheckedChange}
    />
  </div>
)

const BrandingSettings = ({ formData, setFormData, isSaving, handleSave }) => {
  const [uploading, setUploading] = useState({})

  const handleUpload = async (file, field) => {
    setUploading(prev => ({ ...prev, [field]: true }))
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await apiService.post('/settings/branding/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setFormData(prev => ({
        ...prev,
        branding: { ...prev.branding, [field]: res.data.url }
      }))
      toast.success('Image uploaded')
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }))
    }
  }

  const UploadField = ({ label, field, hint }) => (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">{label}</label>
      <div className="flex items-start gap-4">
        <div className="w-20 h-20 rounded-xl border-2 border-dashed border-neutral-300 dark:border-neutral-600 flex items-center justify-center bg-neutral-50 dark:bg-dark-300 shrink-0 overflow-hidden">
          {formData.branding?.[field] ? (
            <img src={formData.branding[field]} alt={label} className="w-full h-full object-contain" />
          ) : (
            <Image className="h-6 w-6 text-neutral-400" />
          )}
        </div>
        <div className="space-y-2 flex-1">
          <label className={cn(
            'flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all duration-200 w-fit',
            'border-neutral-300 dark:border-neutral-600 bg-white dark:bg-dark-300',
            'hover:bg-accent-50 hover:border-accent-300 dark:hover:bg-accent-900/20',
            'text-sm font-medium text-neutral-700 dark:text-neutral-300',
            uploading[field] && 'opacity-50 pointer-events-none'
          )}>
            <Upload className="h-4 w-4" />
            {uploading[field] ? 'Uploading...' : 'Upload image'}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => { if (e.target.files?.[0]) handleUpload(e.target.files[0], field) }}
            />
          </label>
          {hint && <p className="text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>}
          {formData.branding?.[field] && (
            <button
              onClick={() => setFormData(prev => ({ ...prev, branding: { ...prev.branding, [field]: '' } }))}
              className="text-xs text-error-500 hover:underline"
            >
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-8">
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5 text-accent-600" />
              CMS Identity
            </CardTitle>
            <CardDescription>Set the name and login page copy for your CMS</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ModernInput
                label="CMS Name"
                value={formData.branding?.cmsName || ''}
                onChange={e => setFormData(prev => ({ ...prev, branding: { ...prev.branding, cmsName: e.target.value } }))}
                placeholder="WebNest"
                required
              />
              <ModernInput
                label="Support Email"
                type="email"
                value={formData.branding?.supportEmail || ''}
                onChange={e => setFormData(prev => ({ ...prev, branding: { ...prev.branding, supportEmail: e.target.value } }))}
                placeholder="support@youragency.com"
              />
            </div>
            <ModernInput
              label="Login Page Tagline"
              value={formData.branding?.tagline || ''}
              onChange={e => setFormData(prev => ({ ...prev, branding: { ...prev.branding, tagline: e.target.value } }))}
              placeholder="Powering your online presence"
            />
            <ModernInput
              label="Copyright Text"
              value={formData.branding?.copyrightText || ''}
              onChange={e => setFormData(prev => ({ ...prev, branding: { ...prev.branding, copyrightText: e.target.value } }))}
              placeholder="© 2024 WebNest. All rights reserved."
            />
            <ModernInput
              label="App URL"
              type="url"
              value={formData.branding?.appUrl || ''}
              onChange={e => setFormData(prev => ({ ...prev, branding: { ...prev.branding, appUrl: e.target.value } }))}
              placeholder="https://cms.youragency.com"
            />
          </CardContent>
        </Card>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="h-5 w-5 text-accent-600" />
              Logo &amp; Favicon
            </CardTitle>
            <CardDescription>
              Logo appears in the admin sidebar. Favicon appears in browser tabs.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <UploadField
              label="Logo (Dark Mode)"
              field="logoUrl"
              hint="Used on dark backgrounds — white or light-coloured version. PNG or SVG with transparent background."
            />
            <UploadField
              label="Logo (Light Mode)"
              field="logoUrlLight"
              hint="Used on light backgrounds — dark or black version. PNG or SVG with transparent background."
            />
            <UploadField
              label="Favicon"
              field="faviconUrl"
              hint="Recommended: 32×32px or 64×64px ICO, PNG or SVG."
            />
          </CardContent>
        </Card>
      </motion.div>

      <motion.div variants={itemVariants}>
        <div className="flex justify-end">
          <Button onClick={() => handleSave('branding')} disabled={isSaving}>
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Branding'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  )
}

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('branding')
  const [isSaving, setIsSaving] = useState(false)
  const [show2FAModal, setShow2FAModal] = useState(false)
  const [emailTestStatus, setEmailTestStatus] = useState(null) // null | 'testing' | 'success' | 'error'
  const [emailTestMessage, setEmailTestMessage] = useState('')
  const queryClient = useQueryClient()

  const { data: adminSecurityInfo } = useQuery(
    'admin-user-security',
    () => apiService.get('/auth/security').then(r => r.data),
    { staleTime: 0 }
  )

  const { data: settings, isLoading } = useQuery('settings', () => apiService.get('/settings').then(res => res.data))
  
  // Fetch actual billing plans data
  const { data: billingPlans } = useQuery('billing-plans', () => apiService.get('/billing/plans').then(res => res.data))
  
  // Fetch custom plans data
  const { data: customPlans } = useQuery('custom-plans', () => apiService.get('/custom-plans').then(res => res.data))

  const updateSettingsMutation = useMutation(
    (newSettings) => apiService.put('/settings', newSettings).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('settings')
        queryClient.invalidateQueries('billing-plans')
        toast.success('Settings updated successfully')
        setIsSaving(false)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update settings')
        setIsSaving(false)
      }
    }
  )

  // Separate mutation for updating billing plans
  const updatePlansMutation = useMutation(
    (plans) => apiService.put('/billing/plans', plans).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('billing-plans')
        queryClient.invalidateQueries('settings')
        // Force a refetch to get the updated data
        queryClient.refetchQueries('billing-plans')
        toast.success('Plans updated successfully')
        setIsSaving(false)
      },
      onError: (error) => {
        console.error('Update plans error:', error)
        toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to update plans')
        setIsSaving(false)
      }
    }
  )

  // Custom plans mutations
  const createCustomPlanMutation = useMutation(
    (planData) => apiService.post('/custom-plans', planData).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('custom-plans')
        toast.success('Custom plan created successfully')
        setIsSaving(false)
      },
      onError: (error) => {
        console.error('Create custom plan error:', error)
        toast.error(error.response?.data?.message || 'Failed to create custom plan')
        setIsSaving(false)
      }
    }
  )

  const updateCustomPlanMutation = useMutation(
    ({ planId, planData }) => apiService.put(`/custom-plans/${planId}`, planData).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('custom-plans')
        toast.success('Custom plan updated successfully')
        setIsSaving(false)
      },
      onError: (error) => {
        console.error('Update custom plan error:', error)
        toast.error(error.response?.data?.message || 'Failed to update custom plan')
        setIsSaving(false)
      }
    }
  )

  const deleteCustomPlanMutation = useMutation(
    (planId) => apiService.delete(`/custom-plans/${planId}`).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('custom-plans')
        toast.success('Custom plan deleted successfully')
      },
      onError: (error) => {
        console.error('Delete custom plan error:', error)
        toast.error(error.response?.data?.message || 'Failed to delete custom plan')
      }
    }
  )

  // Default settings structure for loading states
  const defaultSettings = {
    general: {
      siteName: 'WebNest CMS',
      siteDescription: 'Professional CMS for managing websites',
      adminEmail: 'admin@webnest.com',
      timezone: 'UTC',
      language: 'en',
      maintenanceMode: false,
      registrationEnabled: true
    },
    security: {
      requireTwoFactor: false,
      sessionTimeout: 24,
      maxLoginAttempts: 5,
      passwordMinLength: 8,
      requirePasswordChange: false,
      forceHttps: true,
      ipWhitelist: [],
      auditLogging: true
    },
    email: {
      provider: 'smtp',
      smtpHost: 'smtp.gmail.com',
      smtpPort: 587,
      smtpUsername: '',
      smtpPassword: '',
      fromEmail: 'noreply@webnest.com',
      fromName: 'WebNest',
      enableTls: true,
      testConnection: 'pending'
    },
    storage: {
      maxFileSize: 10,
      allowedTypes: ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx'],
      storageQuota: 1000,
      backupEnabled: true,
      backupFrequency: 'daily',
      compressionEnabled: true,
      cdnEnabled: false
    },
    appearance: {
      primaryColor: '#4f46e5',
      secondaryColor: '#059669',
      darkMode: true,
      companyLogo: '',
      favicon: '',
      customCss: ''
    },
    branding: {
      cmsName: 'WebNest',
      tagline: 'Powering your online presence',
      supportEmail: '',
      copyrightText: '© 2024 WebNest. All rights reserved.',
      logoUrl: '',
      logoUrlLight: '',
      faviconUrl: '',
      appUrl: ''
    },
    notifications: {
      emailNotifications: true,
      systemAlerts: true,
      maintenanceAlerts: true,
      securityAlerts: true,
      reportFrequency: 'weekly'
    },
    plans: {
      basic: {
        name: 'Basic',
        price: 29,
        features: {
          sites: 1,
          editsPerMonth: 50,
          storage: '1GB Storage',
          support: 'Email Support',
          customDomain: true,
          analytics: false,
          backup: false,
          priority: 'normal'
        }
      },
      pro: {
        name: 'Pro',
        price: 79,
        features: {
          sites: 5,
          editsPerMonth: 200,
          storage: '10GB Storage',
          support: 'Priority Support',
          customDomain: true,
          analytics: true,
          backup: true,
          priority: 'high'
        }
      },
      enterprise: {
        name: 'Enterprise',
        price: 199,
        features: {
          sites: 'unlimited',
          editsPerMonth: 'unlimited',
          storage: '100GB Storage',
          support: '24/7 Phone Support',
          customDomain: true,
          analytics: true,
          backup: true,
          priority: 'highest'
        }
      }
    }
  }

  // Merge real billing plans data with settings
  const displaySettings = useMemo(() => {
    // Use actual settings if available, otherwise use defaults only for initial loading
    const baseSettings = settings || defaultSettings
    
    const result = {
      ...baseSettings,
      plans: billingPlans ? {
        basic: billingPlans.basic || baseSettings.plans.basic,
        pro: billingPlans.pro || baseSettings.plans.pro,
        enterprise: billingPlans.enterprise || baseSettings.plans.enterprise
      } : baseSettings.plans
    }
    return result
  }, [settings, billingPlans])
  
  const [formData, setFormData] = useState(displaySettings)

  // Update formData when billing plans change (but not when saving)
  useEffect(() => {
    if (!isSaving) {
      setFormData(displaySettings)
    }
  }, [displaySettings, isSaving])

  // Show loading state while fetching settings
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Loading settings..." />
      </div>
    )
  }

  const handleSave = async (section) => {
    setIsSaving(true)
    
    if (section === 'plans') {
      // API expects object format, so send the plans object directly
      updatePlansMutation.mutate(formData.plans)
    } else {
      updateSettingsMutation.mutate({
        ...displaySettings,
        [section]: formData[section]
      })
    }
  }

  const handleTestEmailConnection = async () => {
    setEmailTestStatus('testing')
    setEmailTestMessage('')
    try {
      const res = await apiService.get('/emails/verify')
      if (res.data?.verified) {
        setEmailTestStatus('success')
        setEmailTestMessage('Connection successful — your SMTP settings are working.')
      } else {
        setEmailTestStatus('error')
        setEmailTestMessage(res.data?.message || 'Connection failed. Check your SMTP settings.')
      }
    } catch (err) {
      setEmailTestStatus('error')
      setEmailTestMessage(err.response?.data?.details || err.response?.data?.error || 'Could not reach the email server. Check your settings.')
    }
  }

  const tabs = [
    { id: 'branding', name: 'Branding', icon: Palette, description: 'CMS name, logo, favicon and login page settings' },
    { id: 'security', name: 'Security', icon: Shield, description: 'Security and authentication' },
    { id: 'email', name: 'Email', icon: Mail, description: 'Email server configuration' },
    { id: 'storage', name: 'Storage', icon: Database, description: 'File storage and backups' },
    { id: 'plans', name: 'Plans', icon: Key, description: 'Manage client subscription plans' },
    { id: 'custom-plans', name: 'Custom Plans', icon: Key, description: 'Create custom plans for friends, family, or special customers' }
  ]

  const SecuritySettings = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >

      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-accent-600" />
              Your Account 2FA
            </CardTitle>
            <CardDescription>
              Protect your admin account with an authenticator app
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between py-2">
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  Two-Factor Authentication
                </p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {adminSecurityInfo?.totp_enabled
                    ? 'Enabled — your account is protected with an authenticator app'
                    : 'Add an extra layer of security to your account'}
                </p>
              </div>
              <Button
                variant={adminSecurityInfo?.totp_enabled ? 'destructive' : 'outline'}
                onClick={() => setShow2FAModal(true)}
              >
                {adminSecurityInfo?.totp_enabled ? 'Disable 2FA' : 'Enable 2FA'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>


      {show2FAModal && (
        <AdminTwoFactorModal
          isEnabled={!!adminSecurityInfo?.totp_enabled}
          onClose={() => { setShow2FAModal(false); queryClient.invalidateQueries('admin-user-security') }}
        />
      )}
    </motion.div>
  )

  const EmailSettings = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-accent-600" />
              SMTP Configuration
            </CardTitle>
            <CardDescription>
              Configure email server settings for system notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ModernInput
                label="SMTP Host"
                value={formData.email?.smtpHost || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  email: { ...prev.email, smtpHost: e.target.value }
                }))}
                placeholder="smtp.gmail.com"
                required
              />
              <ModernInput
                label="SMTP Port"
                type="number"
                value={formData.email?.smtpPort || 587}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  email: { ...prev.email, smtpPort: parseInt(e.target.value) }
                }))}
                placeholder="587"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ModernInput
                label="Username"
                value={formData.email?.smtpUsername || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  email: { ...prev.email, smtpUsername: e.target.value }
                }))}
                placeholder="your-email@gmail.com"
              />
              <ModernInput
                label="Password"
                type="password"
                value={formData.email?.smtpPassword || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  email: { ...prev.email, smtpPassword: e.target.value }
                }))}
                placeholder="••••••••"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ModernInput
                label="From Email"
                type="email"
                value={formData.email?.fromEmail || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  email: { ...prev.email, fromEmail: e.target.value }
                }))}
                placeholder="noreply@example.com"
                required
              />
              <ModernInput
                label="From Name"
                value={formData.email?.fromName || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  email: { ...prev.email, fromName: e.target.value }
                }))}
                placeholder="WebNest CMS"
                required
              />
            </div>
            <ModernSwitchField
              label="Enable TLS"
              description="Use secure TLS connection for email delivery"
              checked={formData.email?.enableTls || false}
              onCheckedChange={(checked) => setFormData(prev => ({
                ...prev,
                email: { ...prev.email, enableTls: checked }
              }))}
            />

            {/* Test connection */}
            <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center gap-4 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTestEmailConnection}
                  disabled={emailTestStatus === 'testing'}
                >
                  {emailTestStatus === 'testing' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Wifi className="h-4 w-4 mr-2" />
                      Test Connection
                    </>
                  )}
                </Button>

                {emailTestStatus === 'success' && (
                  <div className="flex items-center gap-2 text-sm text-success-600 dark:text-success-400">
                    <CheckCircle className="h-4 w-4 shrink-0" />
                    <span>{emailTestMessage}</span>
                  </div>
                )}
                {emailTestStatus === 'error' && (
                  <div className="flex items-center gap-2 text-sm text-error-600 dark:text-error-400">
                    <WifiOff className="h-4 w-4 shrink-0" />
                    <span>{emailTestMessage}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Button
          onClick={() => handleSave('email')}
          disabled={isSaving}
          className="w-full md:w-auto"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Email Settings
            </>
          )}
        </Button>
      </motion.div>
    </motion.div>
  )

  const StorageSettings = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-accent-600" />
              File Storage
            </CardTitle>
            <CardDescription>
              Configure file upload and storage settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ModernInput
                label="Max File Size (MB)"
                type="number"
                value={formData.storage?.maxFileSize || 10}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  storage: { ...prev.storage, maxFileSize: parseInt(e.target.value) }
                }))}
                placeholder="10"
              />
              <ModernInput
                label="Storage Quota (GB)"
                type="number"
                value={formData.storage?.storageQuota || 1}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  storage: { ...prev.storage, storageQuota: parseInt(e.target.value) }
                }))}
                placeholder="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Allowed File Types
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx', 'txt', 'zip', 'mp4', 'webm', 'svg'].map(type => (
                  <label key={type} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.storage?.allowedTypes?.includes(type) || false}
                      onChange={(e) => {
                        const types = formData.storage?.allowedTypes || []
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            storage: { ...prev.storage, allowedTypes: [...types, type] }
                          }))
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            storage: { ...prev.storage, allowedTypes: types.filter(t => t !== type) }
                          }))
                        }
                      }}
                      className="rounded text-accent-600 focus:ring-accent-500"
                    />
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">{type}</span>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle>Storage Features</CardTitle>
            <CardDescription>
              Advanced storage and optimization settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ModernSwitchField
              label="Automatic Backups"
              description="Enable automatic database and file backups"
              checked={formData.storage?.backupEnabled || false}
              onCheckedChange={(checked) => setFormData(prev => ({
                ...prev,
                storage: { ...prev.storage, backupEnabled: checked }
              }))}
            />
            {formData.storage?.backupEnabled && (
              <div className="ml-4 border-l-2 border-accent-200 pl-4">
                <ModernSelect
                  label="Backup Frequency"
                  value={formData.storage?.backupFrequency || 'daily'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    storage: { ...prev.storage, backupFrequency: e.target.value }
                  }))}
                  options={[
                    { value: 'hourly', label: 'Every Hour' },
                    { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'monthly', label: 'Monthly' }
                  ]}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Button 
          onClick={() => handleSave('storage')} 
          disabled={isSaving}
          className="w-full md:w-auto"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Storage Settings
            </>
          )}
        </Button>
      </motion.div>
    </motion.div>
  )



  const PlansSettings = () => {
    const [editingPlan, setEditingPlan] = useState(null)
    const [planForm, setPlanForm] = useState({
      name: '',
      price: 0,
      stripePriceId: '',
      stripeProductId: '',
      features: {
        sites: 1,
        editsPerMonth: 50,
        storage: '1GB Storage',
        support: 'Email Support',
        customDomain: false,
        analytics: false,
        backup: false,
        priority: 'normal'
      }
    })

    const handleEditPlan = (planId) => {
      const plan = formData.plans[planId]
      setEditingPlan(planId)
      setPlanForm(plan)
    }

    const handleSavePlan = () => {
      // Update the form data
      const updatedPlans = {
        ...formData.plans,
        [editingPlan]: planForm
      }
      
      // Save directly to database
      setIsSaving(true)
      updatePlansMutation.mutate(updatedPlans)
      setEditingPlan(null)
    }

    const handleCancelEdit = () => {
      setEditingPlan(null)
      setPlanForm({
        name: '',
        price: 0,
        stripePriceId: '',
        stripeProductId: '',
        features: {
          sites: 1,
          editsPerMonth: 50,
          storage: '1GB Storage',
          support: 'Email Support',
          customDomain: false,
          analytics: false,
          backup: false,
          priority: 'normal'
        }
      })
    }

    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-8"
      >
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-accent-600" />
                Subscription Plans
              </CardTitle>
              <CardDescription>
                Manage client subscription plans, pricing, and features
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {Object.entries(formData.plans || {}).map(([planId, plan]) => (
                  <div key={planId} className="relative">
                    <Card className={cn(
                      'transition-all duration-200 hover:shadow-lg',
                      editingPlan === planId ? 'ring-2 ring-accent-500' : 'hover:shadow-md'
                    )}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">{plan.name}</CardTitle>
                        <div className="text-2xl font-bold text-accent-600">
                          £{plan.price}
                          <span className="text-sm font-normal text-neutral-500">/month</span>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">Sites</span>
                            <span className="text-sm font-medium">
                              {plan.features.sites === 'unlimited' ? 'Unlimited' : plan.features.sites}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">Edits/Month</span>
                            <span className="text-sm font-medium">
                              {plan.features.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features.editsPerMonth}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">Storage</span>
                            <span className="text-sm font-medium">{plan.features.storage}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">Support</span>
                            <span className="text-sm font-medium">{plan.features.support}</span>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <CheckCircle className={cn(
                                'h-4 w-4',
                                plan.features.customDomain ? 'text-success-500' : 'text-neutral-300'
                              )} />
                              <span className="text-sm text-neutral-600 dark:text-neutral-400">Custom Domain</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <CheckCircle className={cn(
                                'h-4 w-4',
                                plan.features.analytics ? 'text-success-500' : 'text-neutral-300'
                              )} />
                              <span className="text-sm text-neutral-600 dark:text-neutral-400">Analytics</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <CheckCircle className={cn(
                                'h-4 w-4',
                                plan.features.backup ? 'text-success-500' : 'text-neutral-300'
                              )} />
                              <span className="text-sm text-neutral-600 dark:text-neutral-400">Backup</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleEditPlan(planId)}
                          variant="outline"
                          size="sm"
                          className="w-full"
                        >
                          Edit Plan
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {editingPlan && (
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle>Edit Plan: {planForm.name}</CardTitle>
                <CardDescription>
                  Update plan details, pricing, and features
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ModernInput
                    label="Plan Name"
                    value={planForm.name}
                    onChange={(e) => setPlanForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Basic Plan"
                    required
                  />
                  <ModernInput
                    label="Price (Monthly)"
                    type="number"
                    value={planForm.price}
                    onChange={(e) => setPlanForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    placeholder="29"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <ModernInput
                      label="Number of Sites"
                      value={planForm.features.sites === 'unlimited' ? 'unlimited' : planForm.features.sites}
                      onChange={(e) => {
                        const value = e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value) || 1
                        setPlanForm(prev => ({
                          ...prev,
                          features: { ...prev.features, sites: value }
                        }))
                      }}
                      placeholder="1 or 'unlimited'"
                    />
                  </div>
                  <div>
                    <ModernInput
                      label="Edits Per Month"
                      value={planForm.features.editsPerMonth === 'unlimited' ? 'unlimited' : planForm.features.editsPerMonth}
                      onChange={(e) => {
                        const value = e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value) || 50
                        setPlanForm(prev => ({
                          ...prev,
                          features: { ...prev.features, editsPerMonth: value }
                        }))
                      }}
                      placeholder="50 or 'unlimited'"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ModernInput
                    label="Storage"
                    value={planForm.features.storage}
                    onChange={(e) => setPlanForm(prev => ({
                      ...prev,
                      features: { ...prev.features, storage: e.target.value }
                    }))}
                    placeholder="1GB Storage"
                  />
                  <ModernInput
                    label="Support Type"
                    value={planForm.features.support}
                    onChange={(e) => setPlanForm(prev => ({
                      ...prev,
                      features: { ...prev.features, support: e.target.value }
                    }))}
                    placeholder="Email Support"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ModernInput
                    label="Stripe Price ID (Optional)"
                    value={planForm.stripePriceId || ''}
                    onChange={(e) => setPlanForm(prev => ({
                      ...prev,
                      stripePriceId: e.target.value
                    }))}
                    placeholder="price_1234567890"
                  />
                  <ModernInput
                    label="Stripe Product ID (Optional)"
                    value={planForm.stripeProductId || ''}
                    onChange={(e) => setPlanForm(prev => ({
                      ...prev,
                      stripeProductId: e.target.value
                    }))}
                    placeholder="prod_1234567890"
                  />
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-medium text-neutral-900 dark:text-neutral-100">Features</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ModernSwitchField
                      label="Custom Domain"
                      description="Allow custom domain configuration"
                      checked={planForm.features.customDomain || false}
                      onCheckedChange={(checked) => setPlanForm(prev => ({
                        ...prev,
                        features: { ...prev.features, customDomain: checked }
                      }))}
                    />
                    <ModernSwitchField
                      label="Analytics"
                      description="Include analytics and reporting"
                      checked={planForm.features.analytics || false}
                      onCheckedChange={(checked) => setPlanForm(prev => ({
                        ...prev,
                        features: { ...prev.features, analytics: checked }
                      }))}
                    />
                    <ModernSwitchField
                      label="Backup"
                      description="Automatic backup functionality"
                      checked={planForm.features.backup || false}
                      onCheckedChange={(checked) => setPlanForm(prev => ({
                        ...prev,
                        features: { ...prev.features, backup: checked }
                      }))}
                    />
                    <div>
                      <ModernSelect
                        label="Priority Level"
                        value={planForm.features.priority || 'normal'}
                        onChange={(e) => setPlanForm(prev => ({
                          ...prev,
                          features: { ...prev.features, priority: e.target.value }
                        }))}
                        options={[
                          { value: 'normal', label: 'Normal' },
                          { value: 'high', label: 'High' },
                          { value: 'highest', label: 'Highest' }
                        ]}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <Button variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button onClick={handleSavePlan}>
                    Save Plan
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

      </motion.div>
    )
  }

  const CustomPlansSettings = () => {
    const [showCreateForm, setShowCreateForm] = useState(false)
    const [editingCustomPlan, setEditingCustomPlan] = useState(null)
    const [customPlanForm, setCustomPlanForm] = useState({
      name: '',
      price: 0,
      description: '',
      targetType: 'custom',
      targetEmails: [],
      stripePriceId: '',
      stripeProductId: '',
      features: {
        sites: 1,
        editsPerMonth: 50,
        storage: '1GB Storage',
        support: 'Email Support',
        customDomain: false,
        analytics: false,
        backup: false,
        priority: 'normal'
      }
    })
    const [emailInput, setEmailInput] = useState('')

    const resetForm = () => {
      setCustomPlanForm({
        name: '',
        price: 0,
        description: '',
        targetType: 'custom',
        targetEmails: [],
        stripePriceId: '',
        stripeProductId: '',
        features: {
          sites: 1,
          editsPerMonth: 50,
          storage: '1GB Storage',
          support: 'Email Support',
          customDomain: false,
          analytics: false,
          backup: false,
          priority: 'normal'
        }
      })
      setEmailInput('')
    }

    const handleCreateCustomPlan = () => {
      setIsSaving(true)
      createCustomPlanMutation.mutate(customPlanForm)
      setShowCreateForm(false)
      resetForm()
    }

    const handleUpdateCustomPlan = () => {
      setIsSaving(true)
      updateCustomPlanMutation.mutate({ 
        planId: editingCustomPlan, 
        planData: customPlanForm 
      })
      setEditingCustomPlan(null)
      resetForm()
    }

    const handleDeleteCustomPlan = (planId) => {
      if (window.confirm('Are you sure you want to delete this custom plan?')) {
        deleteCustomPlanMutation.mutate(planId)
      }
    }

    const handleEditCustomPlan = (plan) => {
      setEditingCustomPlan(plan.id)
      setCustomPlanForm({
        name: plan.name,
        price: plan.price,
        description: plan.description || '',
        targetType: plan.targetType,
        targetEmails: plan.targetEmails || [],
        stripePriceId: plan.stripePriceId || '',
        stripeProductId: plan.stripeProductId || '',
        features: { ...plan.features }
      })
      setShowCreateForm(true)
    }

    const addEmail = () => {
      if (emailInput.trim() && !customPlanForm.targetEmails.includes(emailInput.trim())) {
        setCustomPlanForm(prev => ({
          ...prev,
          targetEmails: [...prev.targetEmails, emailInput.trim()]
        }))
        setEmailInput('')
      }
    }

    const removeEmail = (email) => {
      setCustomPlanForm(prev => ({
        ...prev,
        targetEmails: prev.targetEmails.filter(e => e !== email)
      }))
    }

    const getTargetTypeIcon = (type) => {
      switch (type) {
        case 'friend': return <Users className="h-4 w-4" />
        case 'family': return <Heart className="h-4 w-4" />
        default: return <Key className="h-4 w-4" />
      }
    }

    const getTargetTypeColor = (type) => {
      switch (type) {
        case 'friend': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
        case 'family': return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200'
        default: return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      }
    }

    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-8"
      >
        {/* Header */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="h-5 w-5 text-accent-600" />
                    Custom Plans
                  </CardTitle>
                  <CardDescription>
                    Create special pricing plans for friends, family, or specific customers
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setShowCreateForm(true)}
                  className="flex items-center gap-2 sm:flex-shrink-0 w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4" />
                  Create Custom Plan
                </Button>
              </div>
            </CardHeader>
          </Card>
        </motion.div>

        {/* Create/Edit Form */}
        {showCreateForm && (
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle>
                  {editingCustomPlan ? 'Edit Custom Plan' : 'Create New Custom Plan'}
                </CardTitle>
                <CardDescription>
                  Set up a custom plan with special pricing and features
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ModernInput
                    label="Plan Name"
                    value={customPlanForm.name}
                    onChange={(e) => setCustomPlanForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Friends & Family Plan"
                    required
                  />
                  <ModernInput
                    label="Price (Monthly)"
                    type="number"
                    value={customPlanForm.price}
                    onChange={(e) => setCustomPlanForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    placeholder="19.99"
                    required
                  />
                </div>

                <ModernTextarea
                  label="Description"
                  value={customPlanForm.description}
                  onChange={(e) => setCustomPlanForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Special pricing for friends and family members"
                  rows={2}
                />

                {/* Target Type */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    Plan Type
                  </label>
                  <div className="flex flex-wrap gap-4">
                    {[
                      { value: 'friend', label: 'Friends', icon: Users },
                      { value: 'family', label: 'Family', icon: Heart },
                      { value: 'custom', label: 'Custom', icon: Key }
                    ].map(({ value, label, icon: Icon }) => (
                      <label key={value} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="targetType"
                          value={value}
                          checked={customPlanForm.targetType === value}
                          onChange={(e) => setCustomPlanForm(prev => ({ ...prev, targetType: e.target.value }))}
                          className="text-accent-600 focus:ring-accent-500"
                        />
                        <Icon className="h-4 w-4" />
                        <span className="text-sm font-medium">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Target Emails */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    Target Emails (Optional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="user@example.com"
                      className="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-dark-300 text-neutral-900 dark:text-neutral-100"
                      onKeyPress={(e) => e.key === 'Enter' && addEmail()}
                    />
                    <Button type="button" onClick={addEmail} variant="outline">
                      Add
                    </Button>
                  </div>
                  {customPlanForm.targetEmails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {customPlanForm.targetEmails.map(email => (
                        <span key={email} className="inline-flex items-center gap-1 px-2 py-1 bg-accent-100 text-accent-800 dark:bg-accent-900 dark:text-accent-200 rounded-md text-sm">
                          {email}
                          <X className="h-3 w-3 cursor-pointer" onClick={() => removeEmail(email)} />
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Features */}
                <div className="space-y-4">
                  <h4 className="font-medium text-neutral-900 dark:text-neutral-100">Plan Features</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ModernInput
                      label="Number of Sites"
                      value={customPlanForm.features.sites === 'unlimited' ? 'unlimited' : customPlanForm.features.sites}
                      onChange={(e) => {
                        const value = e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value) || 1
                        setCustomPlanForm(prev => ({
                          ...prev,
                          features: { ...prev.features, sites: value }
                        }))
                      }}
                      placeholder="1 or 'unlimited'"
                    />
                    <ModernInput
                      label="Edits Per Month"
                      value={customPlanForm.features.editsPerMonth === 'unlimited' ? 'unlimited' : customPlanForm.features.editsPerMonth}
                      onChange={(e) => {
                        const value = e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value) || 50
                        setCustomPlanForm(prev => ({
                          ...prev,
                          features: { ...prev.features, editsPerMonth: value }
                        }))
                      }}
                      placeholder="50 or 'unlimited'"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ModernInput
                      label="Storage"
                      value={customPlanForm.features.storage}
                      onChange={(e) => setCustomPlanForm(prev => ({
                        ...prev,
                        features: { ...prev.features, storage: e.target.value }
                      }))}
                      placeholder="1GB Storage"
                    />
                    <ModernInput
                      label="Support Type"
                      value={customPlanForm.features.support}
                      onChange={(e) => setCustomPlanForm(prev => ({
                        ...prev,
                        features: { ...prev.features, support: e.target.value }
                      }))}
                      placeholder="Email Support"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ModernInput
                      label="Stripe Price ID (Optional)"
                      value={customPlanForm.stripePriceId || ''}
                      onChange={(e) => setCustomPlanForm(prev => ({
                        ...prev,
                        stripePriceId: e.target.value
                      }))}
                      placeholder="price_1234567890"
                    />
                    <ModernInput
                      label="Stripe Product ID (Optional)"
                      value={customPlanForm.stripeProductId || ''}
                      onChange={(e) => setCustomPlanForm(prev => ({
                        ...prev,
                        stripeProductId: e.target.value
                      }))}
                      placeholder="prod_1234567890"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ModernSwitchField
                      label="Custom Domain"
                      description="Allow custom domain configuration"
                      checked={customPlanForm.features.customDomain}
                      onCheckedChange={(checked) => setCustomPlanForm(prev => ({
                        ...prev,
                        features: { ...prev.features, customDomain: checked }
                      }))}
                    />
                    <ModernSwitchField
                      label="Analytics"
                      description="Include analytics and reporting"
                      checked={customPlanForm.features.analytics}
                      onCheckedChange={(checked) => setCustomPlanForm(prev => ({
                        ...prev,
                        features: { ...prev.features, analytics: checked }
                      }))}
                    />
                    <ModernSwitchField
                      label="Backup"
                      description="Automatic backup functionality"
                      checked={customPlanForm.features.backup}
                      onCheckedChange={(checked) => setCustomPlanForm(prev => ({
                        ...prev,
                        features: { ...prev.features, backup: checked }
                      }))}
                    />
                    <ModernSelect
                      label="Priority Level"
                      value={customPlanForm.features.priority}
                      onChange={(e) => setCustomPlanForm(prev => ({
                        ...prev,
                        features: { ...prev.features, priority: e.target.value }
                      }))}
                      options={[
                        { value: 'normal', label: 'Normal' },
                        { value: 'high', label: 'High' },
                        { value: 'highest', label: 'Highest' }
                      ]}
                    />
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      setShowCreateForm(false)
                      setEditingCustomPlan(null)
                      resetForm()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="w-full sm:w-auto"
                    onClick={editingCustomPlan ? handleUpdateCustomPlan : handleCreateCustomPlan}
                    disabled={!customPlanForm.name || customPlanForm.price < 0}
                  >
                    {editingCustomPlan ? 'Update Plan' : 'Create Plan'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Custom Plans List */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle>Existing Custom Plans</CardTitle>
              <CardDescription>
                Manage your existing custom plans
              </CardDescription>
            </CardHeader>
            <CardContent>
              {customPlans && Object.keys(customPlans).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Object.values(customPlans).map(plan => (
                    <div key={plan.id} className="relative">
                      <Card className="hover:shadow-md transition-shadow">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getTargetTypeIcon(plan.targetType)}
                              <CardTitle className="text-lg">{plan.name}</CardTitle>
                            </div>
                            <span className={cn(
                              'px-2 py-1 rounded-full text-xs font-medium',
                              getTargetTypeColor(plan.targetType)
                            )}>
                              {plan.targetType}
                            </span>
                          </div>
                          <div className="text-2xl font-bold text-accent-600">
                            £{plan.price}
                            <span className="text-sm font-normal text-neutral-500">/month</span>
                          </div>
                          {plan.description && (
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">{plan.description}</p>
                          )}
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-neutral-600 dark:text-neutral-400">Sites</span>
                              <span className="text-sm font-medium">
                                {plan.features.sites === 'unlimited' ? 'Unlimited' : plan.features.sites}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-neutral-600 dark:text-neutral-400">Edits/Month</span>
                              <span className="text-sm font-medium">
                                {plan.features.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features.editsPerMonth}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-neutral-600 dark:text-neutral-400">Storage</span>
                              <span className="text-sm font-medium">{plan.features.storage}</span>
                            </div>
                          </div>
                          
                          {plan.targetEmails && plan.targetEmails.length > 0 && (
                            <div className="space-y-1">
                              <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Target Emails:</span>
                              <div className="flex flex-wrap gap-1">
                                {plan.targetEmails.map(email => (
                                  <span key={email} className="text-xs bg-neutral-100 dark:bg-neutral-700 px-2 py-1 rounded">
                                    {email}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div className="flex justify-between gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditCustomPlan(plan)}
                              className="flex-1"
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteCustomPlan(plan.id)}
                              className="flex-1 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Key className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">
                    No Custom Plans Yet
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                    Create your first custom plan to offer special pricing to friends, family, or specific customers.
                  </p>
                  <Button onClick={() => setShowCreateForm(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Custom Plan
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    )
  }

  if (isLoading) {
    return (
      <div className="pt-4">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Loading settings..." />
        </div>
      </div>
    )
  }

  return (
    <div className="pt-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
            Admin Settings
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Configure system settings and preferences to customize your CMS experience
          </p>
        </div>

        {/* Settings Navigation */}
        <Card>
          <CardContent className="p-0">
            <div className="border-b border-neutral-200 dark:border-neutral-700">
              <nav className="flex space-x-0" aria-label="Settings Navigation">
                {tabs.map((tab, index) => {
                  const Icon = tab.icon
                  const isActive = activeTab === tab.id
                  return (
                    <motion.button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={cn(
                        'flex items-center gap-2 px-4 py-4 text-sm font-medium border-b-2 transition-all duration-200 min-w-0 flex-1',
                        isActive
                          ? 'border-accent-600 text-accent-700 dark:text-accent-400 bg-accent-50 dark:bg-accent-900/20'
                          : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-[#2f2b41]'
                      )}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <span className="hidden sm:inline truncate">{tab.name}</span>
                    </motion.button>
                  )
                })}
              </nav>
            </div>

            {/* Active Tab Description */}
            <div className="p-4 bg-neutral-50 dark:bg-[#2f2b41] border-b border-neutral-200 dark:border-[#2f2b41]">
              <p className="text-sm text-neutral-600 dark:text-neutral-300">
                {tabs.find(tab => tab.id === activeTab)?.description}
              </p>
            </div>

            {/* Settings Content */}
            <div className="p-6">
              {activeTab === 'branding' && <BrandingSettings formData={formData} setFormData={setFormData} isSaving={isSaving} handleSave={handleSave} />}
              {activeTab === 'security' && SecuritySettings()}
              {activeTab === 'email' && EmailSettings()}
              {activeTab === 'storage' && StorageSettings()}
              {activeTab === 'plans' && <PlansSettings />}
              {activeTab === 'custom-plans' && <CustomPlansSettings />}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}

const AdminTwoFactorModal = ({ isEnabled, onClose }) => {
  const [step, setStep] = useState(isEnabled ? 'disable' : 'info')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const inputClass = "w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 text-center text-xl tracking-widest"

  const handleSetup = async () => {
    setIsLoading(true)
    setError('')
    try {
      const response = await apiService.post('/auth/2fa/setup')
      setQrCode(response.data.qrCode)
      setSecret(response.data.secret)
      setStep('setup')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to setup 2FA')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerify = async (e) => {
    e?.preventDefault()
    setIsLoading(true)
    setError('')
    try {
      await apiService.post('/auth/2fa/verify', { token: code })
      setStep('success')
      toast.success('2FA enabled successfully!')
      setTimeout(onClose, 2000)
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDisable = async (e) => {
    e?.preventDefault()
    setIsLoading(true)
    setError('')
    try {
      await apiService.post('/auth/2fa/disable', { token: code })
      setStep('disabled')
      toast.success('2FA disabled')
      setTimeout(onClose, 2000)
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid authenticator code')
    } finally {
      setIsLoading(false)
    }
  }

  const ErrorBox = ({ msg }) => msg ? (
    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md flex items-center gap-2">
      <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
      <span className="text-sm text-red-700 dark:text-red-200">{msg}</span>
    </div>
  ) : null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <Card className="inline-block align-bottom text-left overflow-hidden shadow-2xl border-0 transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full bg-white dark:bg-[#1a1625]">
          <CardHeader className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-neutral-900 dark:text-neutral-50">
                Two-Factor Authentication
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={onClose}><X className="h-5 w-5" /></Button>
            </div>
          </CardHeader>
          <CardContent className="px-6 pb-4 space-y-4">
            {step === 'info' && (
              <>
                <p className="text-neutral-600 dark:text-neutral-400">
                  Adds an extra layer of security — you'll need a code from your authenticator app each time you log in.
                </p>
                <ul className="list-disc list-inside text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
                  <li>Google Authenticator</li>
                  <li>Authy</li>
                  <li>Any TOTP-compatible app</li>
                </ul>
                <ErrorBox msg={error} />
              </>
            )}
            {step === 'setup' && (
              <>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.
                </p>
                {qrCode && (
                  <div className="flex justify-center p-4 bg-white rounded-xl border border-neutral-200 dark:border-[#2f2b41]">
                    <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                  </div>
                )}
                <div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Can't scan? Enter this key manually:</p>
                  <code className="block p-2 bg-neutral-100 dark:bg-[#201d2e] rounded text-xs break-all font-mono">{secret}</code>
                </div>
                <form onSubmit={handleVerify} className="space-y-3">
                  <input type="text" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className={inputClass} placeholder="000000" autoFocus required />
                  <ErrorBox msg={error} />
                </form>
              </>
            )}
            {step === 'disable' && (
              <>
                <p className="text-neutral-600 dark:text-neutral-400">
                  Enter the current 6-digit code from your authenticator app to disable 2FA.
                </p>
                <form onSubmit={handleDisable} className="space-y-3">
                  <input type="text" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className={inputClass} placeholder="000000" autoFocus required />
                  <ErrorBox msg={error} />
                </form>
              </>
            )}
            {(step === 'success' || step === 'disabled') && (
              <div className="text-center py-4 space-y-2">
                <Check className="h-12 w-12 text-green-500 mx-auto" />
                <p className="font-medium text-neutral-900 dark:text-neutral-50">
                  {step === 'success' ? '2FA enabled successfully!' : '2FA has been disabled'}
                </p>
              </div>
            )}
          </CardContent>
          {!['success', 'disabled'].includes(step) && (
            <div className="bg-neutral-50/50 dark:bg-[#1f1c2c] border-t border-neutral-200 dark:border-[#2f2b41] px-6 py-3 flex justify-end space-x-3">
              {step === 'info' && (
                <>
                  <Button variant="outline" onClick={onClose}>Cancel</Button>
                  <Button onClick={handleSetup} disabled={isLoading}>
                    {isLoading ? 'Loading…' : 'Get Started'}
                  </Button>
                </>
              )}
              {step === 'setup' && (
                <>
                  <Button variant="outline" onClick={() => setStep('info')}>Back</Button>
                  <Button onClick={handleVerify} disabled={isLoading || code.length !== 6}>
                    {isLoading ? 'Verifying…' : 'Verify & Enable'}
                  </Button>
                </>
              )}
              {step === 'disable' && (
                <>
                  <Button variant="outline" onClick={onClose}>Cancel</Button>
                  <Button variant="destructive" onClick={handleDisable} disabled={isLoading || code.length !== 6}>
                    {isLoading ? 'Disabling…' : 'Disable 2FA'}
                  </Button>
                </>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

export default AdminSettings